<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount, watch, computed } from 'vue';
import InputText from 'primevue/inputtext';
import TreeSelect from 'primevue/treeselect';
import { TreeNode } from 'primevue/treenode';
import { TreeValue } from '../../interfaces.ts';
import { debounce } from '../../functions.ts';

const props = defineProps({
  urlToBuy: {
    type: String,
    required: true
  },
  urlForSale: {
    type: String,
    required: true
  },
  urlGetOptions: {
    type: String,
    required: true
  },
  urlResults: {
    type: String,
    required: true
  },
  initialCount: {
    type: Number,
    default: 0
  },
  trans: { type: Object, required: true },
});

const purpose = ref('1');
let search = ref('');
let region = ref();
let regionOptions = ref<TreeNode[]>([]);
let regionValues = ref<Record<string, TreeValue>>({});
let sectorOptions = ref<TreeNode[]>([]);
let sector = ref<string[]>([]);
let companiesCount = ref(props.initialCount);
let isSearchFocused = ref(false);
let expandedKeys = ref<Record<string, boolean>>({});

const fetchCompaniesCount = () => {
  const data = {
    type: purpose.value,
    search: sector.value.length > 0 ? '' : search.value,
    regions: region.value,
    sectors: sector.value,
    limit: 0,
    count: true,
  };

  fetch(props.urlResults, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(data)
  })
      .then(response => response.json())
      .then(data => {
        companiesCount.value = data.total;
      })
      .catch(error => {
        console.error('Error fetching companies count:', error);
      });
};

const debouncedFetchCompaniesCount = debounce(fetchCompaniesCount, 300);

onMounted(() => {
  fetch(props.urlGetOptions)
      .then(response => response.json())
      .then(data => {
        regionOptions.value = data.regions;
        // Populate expandedKeys with all node keys
        const expandAllKeys = (nodes: TreeNode[]) => {
          nodes.forEach(node => {
            expandedKeys.value[node.key] = true;
            if (node.children) {
              expandAllKeys(node.children);
            }
          });
        };
        expandAllKeys(regionOptions.value);
        sectorOptions.value = data.sectors;
      })
      .catch(error => {
        console.error('Error fetching region options:', error);
      });

  if (props.initialCount < 1) {
    fetchCompaniesCount();
  }

  document.addEventListener('click', handleClickOutside);
});

onBeforeUnmount(() => {
  document.removeEventListener('click', handleClickOutside);
});

watch(regionValues, (newValues) => {
  region.value = Object.keys(newValues).filter(key => newValues[key].checked);
});

watch([purpose, search, region], debouncedFetchCompaniesCount);

const debouncedSearchWatcher = debounce((newSearch) => {
  // If the search input is empty, we want to show all sectors
  if (!newSearch) {
    sector.value = [];
    debouncedFetchCompaniesCount();
    return;
  }

  sector.value = sectorOptions.value
      .filter(option => (option.label ?? '').toLowerCase() == newSearch.toLowerCase())
      .map(option => option.key);
  debouncedFetchCompaniesCount();
}, 300);

watch(search, debouncedSearchWatcher);

const handleSubmit = (event: Event) => {
  event.preventDefault();
  const url = purpose.value === '1' ? props.urlForSale : props.urlToBuy;
  const params = new URLSearchParams({
    search: sector.value.length > 0 ? '' : search.value,
    regions: region.value ? region.value.join(',') : '',
    sectors: sector.value.length > 0 ? sector.value.join(',') : '',
  }).toString();
  window.location.href = `${url}?${params}`;
};

const filteredSectorOptions = computed(() => {
  return sectorOptions.value.filter(option =>
      (option.label ?? '').toLowerCase().includes(search.value.toLowerCase())
  );
});

const hasMatchingOptions = computed(() => {
  return filteredSectorOptions.value.length > 0;
});

const debouncedIsSearchFocusedWatcher = debounce((newIsSearchFocused) => {
  isSearchFocused.value = newIsSearchFocused;
}, 300);

watch(isSearchFocused, debouncedIsSearchFocusedWatcher);

const prefillSearch = (value: string) => {
  search.value = value;
  isSearchFocused.value = false;
};

const handleClickOutside = (event: MouseEvent) => {
  const searchBar = document.querySelector('.search-bar');
  if (searchBar && !searchBar.contains(event.target as Node)) {
    isSearchFocused.value = false;
  }
};

const treeSelectRef = ref();
const onPanelShow = () => {
  if (region.value?.length) {
    return;
  }

  const instance = treeSelectRef.value;
  if (!instance) {
    return;
  }

  const panelElement = instance.overlay;
  if (!panelElement) {
    return;
  }

  // Select first .p-tree-node-content
  const firstNode = panelElement.querySelector('.p-tree-node');
  if (firstNode) {
    // Add a class to disable focus styles
    firstNode.classList.add('no-focus-visible');
    // Add event listener to re-enable focus
    firstNode.addEventListener('focusin', () => {
      firstNode.classList.remove('no-focus-visible');
    });
  }

  const scrollableContainer = panelElement.querySelector('.p-treeselect-tree-container');

  if (scrollableContainer) {
    scrollableContainer.scrollTop = 0;
  }
};
</script>

<template>
  <form class="search-bar" @submit="handleSubmit">
    <span class="purpose">
      <label><input v-model="purpose" name="purpose" type="radio" value="1"> {{ trans.search_bar.label.to_buy }}</label>
      <label><input v-model="purpose" name="purpose" type="radio" value="2"> {{ trans.search_bar.label.for_sale }}</label>
    </span>

    <div class="search-bar__search">
      <InputText v-model="search" :placeholder="trans.search_bar.placeholder.search" @focus="isSearchFocused = true" @blur="isSearchFocused = false" />
      <div v-if="search && hasMatchingOptions && isSearchFocused" class="search-bar-suggestions">
        <div class="search-bar-suggestions__items">
          <div class="search-bar-suggestion__item" v-for="item in filteredSectorOptions" :key="item.key" @mousedown="prefillSearch(item.label ?? '')">
            <span>{{ item.parent_label }} ></span> {{ item.child_label }}
          </div>
        </div>
        <button @mousedown.prevent="handleSubmit" class="btn-solid icon-rarr" @mousedown="isSearchFocused = false">{{ trans.search_bar.label.show_all_results }}</button>
      </div>
    </div>

    <TreeSelect v-model="regionValues"
                :options="regionOptions"
                selectionMode="checkbox"
                :expandedKeys="expandedKeys"
                :placeholder="trans.search_bar.placeholder.region"
                ref="treeSelectRef"
                @show="onPanelShow"
    >
      <template #value>
        <span>{{ region && region.length > 0 ? trans.search_bar.label.num_selected.replace('{num}', region.length.toString()) : trans.search_bar.placeholder.region }}</span>
      </template>
    </TreeSelect>

    <button class="btn-solid icon-rarr">
      {{ purpose === '1'
        ? trans.search_bar.label.show_companies.replace('{num}', companiesCount.toString())
        : trans.search_bar.label.show_profiles.replace('{num}', companiesCount.toString())
      }}
    </button>
  </form>
</template>
